import { useState, useRef, useEffect, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";

import { getLoggedInUserId, getCaseId, getClientId } from "../../Utils/helper";
import api, { api_without_cancellation } from "../../api/api";

import WPBase from "./WPBase";
import SendEmailPopUp from "./SendEmailPopUp";

function WPGenerateDocument({
  docId,
  updatedDocId,
  dynamicTemplateId,
  dispatch,
  state,
  draftId,
  fetchEditDocs,
  type,
  isFromLetterPopup = false,
  container,
  setTotalPages,
  setPageNumber,
  height = 5,
}) {
  const origin = process.env.REACT_APP_BACKEND_URL;

  const userId = getLoggedInUserId();

  const [docName, setDocName] = useState(null);
  const [pageId, setPageId] = useState(null);

  const [emailPopUp, setEmailPopUp] = useState(false);
  // eslint-disable-next-line unused-imports/no-unused-vars
  const [clientData, setClientData] = useState(null);
  // eslint-disable-next-line unused-imports/no-unused-vars
  const [dropdownId, setDropdownId] = useState(null);

  // Inactivity timer state
  const [inactivityTimer, setInactivityTimer] = useState(3600); // 120 seconds = 2 minutes
  const [showTimerWarning, setShowTimerWarning] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const timerIntervalRef = useRef(null);
  const inactivityTimeoutRef = useRef(null);

  const fetchClientPage = async () => {
    try {
      const response = await api.get(
        `${origin}/api/client-page/client/${getClientId()}/case/${getCaseId()}/`
      );

      if (response.status === 200) {
        setClientData(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch client data:", error);
    }
  };

  useEffect(() => {
    fetchClientPage();
  }, []);

  // Store redirect URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get("redirect_url");
    if (redirect) {
      setRedirectUrl(decodeURIComponent(redirect));
    }
  }, []);

  // Reset timer function
  const resetInactivityTimer = useCallback(() => {
    setInactivityTimer(3600); // Reset to 2 minutes
    setShowTimerWarning(false);

    // Clear existing timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }

    // Start new timer
    timerIntervalRef.current = setInterval(() => {
      setInactivityTimer((prev) => {
        const newTime = prev - 1;

        // Show warning at 15 seconds
        if (newTime === 15) {
          setShowTimerWarning(true);
        }

        // Time's up - close document
        if (newTime <= 0) {
          clearInterval(timerIntervalRef.current);
          handleTimerExpiry();
          return 0;
        }

        return newTime;
      });
    }, 1000); // Update every second
  }, []);

  // Timer expiry handler
  const handleTimerExpiry = async () => {
    await deleteEditDocWithPopUp();

    // Redirect to stored URL or default
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      window.history.back();
    }
  };

  // Handle timer warning close
  const handleCloseDocument = async () => {
    setShowTimerWarning(false);
    await handleTimerExpiry();
  };

  // Handle timer warning cancel
  const handleCancelWarning = () => {
    setShowTimerWarning(false);
    resetInactivityTimer(); // Reset to full 2 minutes
  };

  // Start timer on mount
  // useEffect(() => {
  //   resetInactivityTimer();

  //   return () => {
  //     // Cleanup on unmount
  //     if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
  //     if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
  //   };
  // }, [resetInactivityTimer]);

  useEffect(() => {
    resetInactivityTimer();

    const handleUserActivity = () => {
      resetInactivityTimer();
    };

    window.addEventListener("mousemove", handleUserActivity);
    window.addEventListener("mousedown", handleUserActivity);
    window.addEventListener("keydown", handleUserActivity);
    window.addEventListener("scroll", handleUserActivity);
    window.addEventListener("touchstart", handleUserActivity);

    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);

      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("mousedown", handleUserActivity);
      window.removeEventListener("keydown", handleUserActivity);
      window.removeEventListener("scroll", handleUserActivity);
      window.removeEventListener("touchstart", handleUserActivity);
    };
  }, [resetInactivityTimer]);

  // Helper function to send document data back to AddLetterPopup and close tab
  const sendDocumentToLetterPopupAndClose = (finalDocId, description) => {
    if (isFromLetterPopup) {
      localStorage.setItem(
        "letterDocumentCompleted",
        JSON.stringify({
          doc_id: finalDocId,
          file_name: docName,
          description: description,
        })
      );
      // Small delay to ensure localStorage is written before closing
      setTimeout(() => {
        window.close();
      }, 100);
    }
  };

  const deleteEditDoc = async ({ flush = true } = {}) => {
    try {
      // Persist any edits still inside the autosave debounce window before
      // releasing the edit lock (skipped when a manual save just ran)
      if (flush && container.current) {
        try {
          const content = await container.current.save();
          if (content && content.byteLength > 0) {
            const file = new File([content], "autosave.docx", {
              type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });
            const flushData = new FormData();
            flushData.append("doc_id", docId);
            flushData.append("file", file);
            await api_without_cancellation.post(`${origin}/api/update-edit-doc/`, flushData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });
          }
        } catch (error) {
          console.error("Error flushing document before close:", error);
        }
      }

      const formData = new FormData();
      formData.append("doc_id", docId);

      const response = await api_without_cancellation.post(
        `${origin}/api/delete-edit-doc/`,
        formData
      );
      if (response.status === 200) {
        if (response.data.success) {
          fetchEditDocs();
        }
      }
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  const deleteEditDocWithPopUp = async () => {
    try {
      await deleteEditDoc();
      dispatch({
        type: "SHOW_MODAL",
        payload: `${state.tempName} closed successfully!`,
      });
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  const saveDraft = async () => {
    try {
      if (!container.current) {
        console.error("Editor not ready");
        return;
      }

      const content = await container.current.save();

      if (!content || content.byteLength === 0) {
        console.error("Error: Document content is undefined or invalid.");
        return;
      }
      const file = new File([content], `${docName || "document"}.docx`, {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const formData = new FormData();
      formData.append("file", file);
      formData.append("id", docId);
      formData.append("user_id", userId);
      formData.append("dynamic_template_id", dynamicTemplateId);
      formData.append("file_name", docName);
      if (draftId) {
        formData.append("draft_id", draftId);
      }

      const response = await api_without_cancellation.post(
        `${origin}/api/updateDraftDoc/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        // Reset inactivity timer on successful save
        resetInactivityTimer();

        // updateDraftDoc just saved the newest content; no flush needed
        deleteEditDoc({ flush: false });

        // Only show modal if NOT from letter popup (since we'll close the tab)
        if (!isFromLetterPopup) {
          if (type === "GenerateDocument") {
            dispatch({
              type: "SHOW_MODAL",
              payload: `${state.tempName} Draft created successfully!`,
            });
          } else {
            dispatch({
              type: "SHOW_MODAL",
              payload: `${state.tempName} Draft updated successfully!`,
            });
          }
        }

        // Return the doc_id from the response (this is the DocumentDraft doc_id)
        return response.data.doc_id || null;
      }
    } catch (error) {
      console.error("Error saving document:", error);
      return null;
    }
  };

  const downloadDocument = async () => {
    try {
      if (!container.current) {
        console.error("Editor not ready");
        return;
      }

      const content = await container.current.save();

      if (!content || content.byteLength === 0) {
        console.error("Error: Document content is empty.");
        return;
      }

      const blob = new Blob([content], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${docName || "document"}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // free memory
    } catch (error) {
      console.error("Error downloading document:", error);
    }
  };

  const printDoc = async () => {
    if (!container.current) {
      console.error("Editor not ready");
      return;
    }

    const content = await container.current.save();

    if (!content || content.byteLength === 0) {
      console.error("Error: Document content is empty.");
      return;
    }

    const renamedFile = new File([content], `${docName || "document"}.docx`, {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    const formData = new FormData();
    formData.append("file", renamedFile);
    try {
      const response = await api.post(`${origin}/api/documents/word-to-pdf/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const { data } = response.data;
      const pdfBlob = new Blob([Uint8Array.from(atob(data), (c) => c.charCodeAt(0))], {
        type: "application/pdf",
      });
      const url = URL.createObjectURL(pdfBlob);
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        iframe.addEventListener("afterprint", () => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(url);
        });
      };
    } catch (error) {
      console.error("Error printing document:", error);
    }
  };

  const attachPdfToCaseFile = async (draftIdParam = null) => {
    if (!container.current) {
      console.error("Editor not ready");
      return;
    }

    const content = await container.current.save();

    if (!content || content.byteLength === 0) {
      console.error("Error: Document content is empty.");
      return;
    }

    const renamedFile = new File([content], `${docName || "document"}.docx`, {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    // const content = await container.current?.documentEditor.saveAsBlob("Docx");
    // const renamedFile = new File([content], `${docName}.docx`, {
    //   type: content.type,
    // });

    const formData = new FormData();
    formData.append("file", renamedFile);
    formData.append("case_id", getCaseId());
    formData.append("client_id", getClientId());
    formData.append("page_id", pageId);

    // Add draft_id if provided
    if (draftIdParam) {
      formData.append("draft_id", draftIdParam);
    }

    try {
      const response = await api_without_cancellation.post(
        `${origin}/api/documents/pdf-to-casefile/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.status === 200) {
        const { data, filename } = response.data;
        const pdfBlob = new Blob([Uint8Array.from(atob(data), (c) => c.charCodeAt(0))], {
          type: "application/pdf",
        });
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error printing document:", error);
    }
  };
  const pdfToCaseFile = async () => {
    try {
      // First attach PDF to case file (this uses the current document content to generate PDF)
      await attachPdfToCaseFile(draftId);

      // Then save the draft to get the proper doc_id from DocumentDrafts
      // Pass showModal: false to prevent showing the draft save modal
      const savedDocId = await saveDraft();

      if (!savedDocId && isFromLetterPopup) {
        console.error("Failed to save draft - cannot proceed");
        return;
      }

      // If from letter popup, send the SAVED document data back and close
      if (isFromLetterPopup && savedDocId) {
        sendDocumentToLetterPopupAndClose(savedDocId, `PDF attached to case: ${docName}`);
      } else if (!isFromLetterPopup) {
        dispatch({
          type: "SHOW_MODAL",
          payload: `${docName} Attached To Case Successfully`,
        });
      }
    } catch (error) {
      console.error("Error printing document:", error);
    }
  };

  const sendEmail = () => {
    setEmailPopUp(true);
  };

  const controls = (
    <>
      <div>
        <div>
          <button
            type="button"
            className="btn btn-primary fw-bold ms-auto m-b-5 w-100"
            onClick={pdfToCaseFile}
          >
            PDF to Case File
          </button>
        </div>
      </div>
      <div>
        <div>
          <button
            type="button"
            className="btn btn-primary fw-bold ms-auto m-b-5 w-100"
            onClick={deleteEditDocWithPopUp}
          >
            Close
          </button>
        </div>
      </div>

      <div>
        <button
          type="button"
          className="btn btn-primary fw-bold ms-auto m-b-5 w-100"
          onClick={printDoc}
        >
          Print
        </button>
      </div>
      <div>
        <button
          type="button"
          className="btn btn-primary fw-bold ms-auto m-b-5 w-100"
          onClick={downloadDocument}
        >
          Download
        </button>
      </div>
      <div>
        <button
          type="button"
          className="btn btn-primary fw-bold ms-auto m-b-5 w-100"
          onClick={sendEmail}
        >
          Email To Client
        </button>
      </div>
    </>
  );

  const handleCloseEmailPopUp = () => {
    setEmailPopUp(false);
  };

  return (
    <>
      <WPBase
        docId={docId}
        updatedDocId={updatedDocId}
        dynamicTemplateId={dynamicTemplateId}
        setPageId={setPageId}
        setDropdownId={setDropdownId}
        setDocName={setDocName}
        state={state}
        dispatch={dispatch}
        container={container}
        controls={controls}
        type={type}
        clientData={clientData}
        print={printDoc}
        download={downloadDocument}
        sendEmail={sendEmail}
        deleteDoc={deleteEditDocWithPopUp}
        draftId={draftId}
        setPageNumber={setPageNumber}
        setTotalPages={setTotalPages}
        height2={height}
      />

      <SendEmailPopUp
        emailPopUp={emailPopUp}
        handleCloseEmailPopUp={handleCloseEmailPopUp}
        attachPdfToCaseFile={attachPdfToCaseFile}
        container={container}
        docName={docName}
        isFromLetterPopup={isFromLetterPopup}
        saveDraft={saveDraft}
        onEmailSent={(savedDocId) => {
          if (isFromLetterPopup && savedDocId) {
            sendDocumentToLetterPopupAndClose(savedDocId, `Email sent to client: ${docName}`);
          }
        }}
      />

      {/* Inactivity Timer Warning Modal */}
      <Modal show={showTimerWarning} onHide={handleCancelWarning} backdrop="static" centered>
        <Modal.Header>
          <Modal.Title>⚠️ Document Will Close Soon</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">Your document will automatically close due to inactivity in:</p>
          <h2 className="text-center text-danger mb-3">{inactivityTimer} seconds</h2>
          <p className="text-muted small">
            The document has been saved. Click &ldquo;Cancel&rdquo; to continue editing, or
            &ldquo;Close Document&rdquo; to close now.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelWarning}>
            Cancel (Keep Editing)
          </Button>
          <Button variant="danger" onClick={handleCloseDocument}>
            Close Document
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default WPGenerateDocument;
